/**
 * Phase 101ea.12 (Feb 2026) — Hook PWA pour /tournee.
 *
 * Objectifs :
 *   1. Swap la balise <link rel="manifest"> vers `manifest-tournee.json` quand
 *      l'utilisateur est sur /tournee/* → si l'utilisateur clique "Ajouter à
 *      l'écran d'accueil" à ce moment-là, il obtient une icône dédiée
 *      "AM Tournée" qui ouvre directement l'app en mode fullscreen sur la
 *      liste des tournées. Le manifest de base (boutique) est restauré à la
 *      sortie du /tournee.
 *
 *   2. Capture l'événement `beforeinstallprompt` pour permettre à l'app de
 *      déclencher un vrai prompt d'installation via une action utilisateur
 *      (bouton "Installer sur mon téléphone").
 *
 *   3. Détecte si déjà installée (mode `standalone`) pour cacher le bouton.
 */
import { useEffect, useState, useCallback } from "react";

const DEFAULT_MANIFEST = "/manifest.json";
const TOURNEE_MANIFEST = "/manifest-tournee.json";

function setManifest(href) {
  const link = document.querySelector('link[rel="manifest"]');
  if (link) {
    const abs = new URL(href, window.location.origin).href;
    if (link.href !== abs) {
      link.href = href;
    }
  }
}

/**
 * Hook à utiliser depuis n'importe quel composant sous /tournee/*.
 * Retourne :
 *   - canInstall : true si un prompt d'installation est disponible
 *   - isInstalled : true si déjà en mode standalone (PWA installée)
 *   - triggerInstall() : déclenche le prompt natif
 */
export function useTourneePWA() {
  const [installEvent, setInstallEvent] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  // Swap manifest tournée dès l'entrée
  useEffect(() => {
    setManifest(TOURNEE_MANIFEST);
    return () => setManifest(DEFAULT_MANIFEST);
  }, []);

  // Détection mode standalone (PWA installée)
  useEffect(() => {
    const mq = window.matchMedia("(display-mode: standalone)");
    const check = () => setIsInstalled(mq.matches || window.navigator.standalone === true);
    check();
    mq.addEventListener?.("change", check);
    return () => mq.removeEventListener?.("change", check);
  }, []);

  // Capture de l'événement d'installation
  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    const onInstalled = () => {
      setInstallEvent(null);
      setIsInstalled(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const triggerInstall = useCallback(async () => {
    if (!installEvent) return { outcome: "no-prompt" };
    await installEvent.prompt();
    const choice = await installEvent.userChoice;
    setInstallEvent(null);
    return choice;
  }, [installEvent]);

  return {
    canInstall: !!installEvent && !isInstalled,
    isInstalled,
    triggerInstall,
  };
}
